import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import './Users.css';

const API_BASE_URL = '/api';

// Icon Components
const ViewIcon = () => (
  <svg width="18px" height="18px" strokeWidth="1.4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" color="currentColor">
    <path d="M3 13C6.6 5 17.4 5 21 13" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"></path>
    <path d="M12 17C10.3431 17 9 15.6569 9 14C9 12.3431 10.3431 11 12 11C13.6569 11 15 12.3431 15 14C15 15.6569 13.6569 17 12 17Z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"></path>
  </svg>
);

const CheckIcon = () => (
  <svg width="18px" height="18px" strokeWidth="1.4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" color="currentColor">
    <path d="M5 13L9 17L19 7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"></path>
  </svg>
);

const CloseIcon = () => (
  <svg width="14px" height="14px" strokeWidth="2" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" color="currentColor">
    <path d="M6 18L18 6M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
  </svg>
);

const BanIcon = () => (
  <svg width="18px" height="18px" strokeWidth="1.4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" color="currentColor">
    <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"></path>
    <path d="M4.92896 4.92896L19.0711 19.0711" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"></path>
  </svg>
);

const NoteIcon = () => (
  <svg width="18px" height="18px" strokeWidth="1.4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" color="currentColor">
    <path d="M3 20.2895V5C3 3.89543 3.89543 3 5 3H19C20.1046 3 21 3.89543 21 5V15C21 16.1046 20.1046 17 19 17H7.96125C7.35368 17 6.77906 17.2762 6.39951 17.7506L4.06852 20.6643C3.71421 21.1072 3 20.8567 3 20.2895Z" stroke="currentColor" strokeWidth="1.4"></path>
  </svg>
);

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [verificationFilter, setVerificationFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [totalPages, setTotalPages] = useState(1);

  // Modals
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [actionType, setActionType] = useState('');
  const [actionData, setActionData] = useState({ notes: '', reason: '' });

  // Image Viewer Modal
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [viewerImage, setViewerImage] = useState({ url: '', title: '' });
  const [imageZoom, setImageZoom] = useState(1);

  // Stats - Initialize with default values to prevent undefined errors
  const [doctorStats, setDoctorStats] = useState({
    total: 0,
    pending: 0,
    in_review: 0,
    approved: 0,
    rejected: 0,
    blocked: 0
  });

  const [studentStats, setStudentStats] = useState({
    total: 0,
    pending: 0,
    in_review: 0,
    approved: 0,
    rejected: 0,
    blocked: 0
  });

  useEffect(() => {
    fetchUsers();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, statusFilter, verificationFilter, searchTerm]);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: currentPage,
        limit: itemsPerPage,
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(verificationFilter !== 'all' && { verification_status: verificationFilter }),
        ...(searchTerm && { search: searchTerm })
      });

      const response = await axios.get(
        `${API_BASE_URL}/admin/user-review/users/pending?${params}`,
        { withCredentials: true }
      );

      if (response.data.success && response.data.data) {
        setUsers(response.data.data.users || []);
        setTotalPages(response.data.data.pagination?.totalPages || 1);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/admin/user-review/stats`,
        { withCredentials: true }
      );

      if (response.data.success && response.data.data) {
        if (response.data.data.doctors) {
          setDoctorStats(response.data.data.doctors);
        }
        if (response.data.data.medical_students) {
          setStudentStats(response.data.data.medical_students);
        }
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
      // Keep default stats on error
    }
  };

  const handleViewUser = async (userId) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/admin/user-review/users/${userId}`,
        { withCredentials: true }
      );

      console.log('User Details API Response:', response.data);
      console.log('Documents:', response.data.data?.documents);

      if (response.data.success && response.data.data) {
        // Flatten the data structure for easier access
        const userData = {
          ...response.data.data.user,
          documents: response.data.data.documents,
          notes: response.data.data.notes,
          activity: response.data.data.activity
        };
        console.log('Setting selectedUser:', userData);
        console.log('Documents count:', userData.documents?.length);
        setSelectedUser(userData);
        setShowDetailModal(true);
      }
    } catch (err) {
      console.error('Error fetching user details:', err);
      toast.error('Failed to load user details');
    }
  };

  const handleAction = (user, action) => {
    setSelectedUser(user);
    setActionType(action);
    setActionData({ notes: '', reason: '' });
    setShowActionModal(true);
  };

  const submitAction = async () => {
    if (!selectedUser) return;

    const endpoints = {
      approve: `/admin/user-review/users/${selectedUser.id}/approve`,
      reject: `/admin/user-review/users/${selectedUser.id}/reject`,
      suspend: `/admin/user-review/users/${selectedUser.id}/suspend`,
      reactivate: `/admin/user-review/users/${selectedUser.id}/reactivate`,
      note: `/admin/user-review/users/${selectedUser.id}/notes`
    };

    const payloads = {
      approve: { notes: actionData.notes },
      reject: { reason: actionData.reason },
      suspend: { reason: actionData.reason },
      reactivate: { notes: actionData.notes },
      note: { note: actionData.notes }
    };

    try {
      const response = await axios.post(
        `${API_BASE_URL}${endpoints[actionType]}`,
        payloads[actionType],
        { withCredentials: true }
      );

      if (response.data.success) {
        toast.success(response.data.message);
        setShowActionModal(false);
        fetchUsers();
        fetchStats();
      }
    } catch (err) {
      console.error('Error performing action:', err);
      toast.error(err.response?.data?.message || 'Action failed');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending_review: { class: 'badge-warning', text: 'Pending Review' },
      in_review: { class: 'badge-info', text: 'In Review' },
      approved: { class: 'badge-success', text: 'Approved' },
      rejected: { class: 'badge-danger', text: 'Rejected' },
      blocked: { class: 'badge-dark', text: 'Blocked' }
    };
    const badge = badges[status] || { class: '', text: status };
    return <span className={`status-badge ${badge.class}`}>{badge.text}</span>;
  };

  const getVerificationBadge = (status) => {
    const badges = {
      not_submitted: { class: 'badge-secondary', text: 'Not Submitted' },
      submitted: { class: 'badge-warning', text: 'Submitted' },
      verified: { class: 'badge-success', text: 'Verified' },
      rejected: { class: 'badge-danger', text: 'Rejected' }
    };
    const badge = badges[status] || { class: '', text: status };
    return <span className={`status-badge ${badge.class}`}>{badge.text}</span>;
  };

  // Image Viewer Functions
  const handleImageClick = (url, title) => {
    setViewerImage({ url, title });
    setImageZoom(1);
    setShowImageViewer(true);
  };

  const handleCloseImageViewer = () => {
    setShowImageViewer(false);
    setViewerImage({ url: '', title: '' });
    setImageZoom(1);
  };

  const handleZoomIn = () => {
    setImageZoom(prev => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setImageZoom(prev => Math.max(prev - 0.25, 0.5));
  };

  const handleResetZoom = () => {
    setImageZoom(1);
  };

  return (
    <div className="users-container">
      <div className="page-header">
        <div>
          <h1>User Management</h1>
          <p className="page-subtitle">Manage and verify user accounts</p>
        </div>
      </div>

      {/* Doctor Stats Grid */}
      <div className="stats-section">
        <h2 className="stats-section-title">Doctors</h2>
        <div className="stats-grid">
          <div className="stat-card">
            <h3>Total Doctors</h3>
            <p className="stat-value">{doctorStats.total || 0}</p>
          </div>
          <div className="stat-card pending">
            <h3>Pending</h3>
            <p className="stat-value">{doctorStats.pending || 0}</p>
          </div>
          <div className="stat-card in-review">
            <h3>In Review</h3>
            <p className="stat-value">{doctorStats.in_review || 0}</p>
          </div>
          <div className="stat-card approved">
            <h3>Approved</h3>
            <p className="stat-value">{doctorStats.approved || 0}</p>
          </div>
          <div className="stat-card rejected">
            <h3>Rejected</h3>
            <p className="stat-value">{doctorStats.rejected || 0}</p>
          </div>
          <div className="stat-card blocked">
            <h3>Blocked</h3>
            <p className="stat-value">{doctorStats.blocked || 0}</p>
          </div>
        </div>
      </div>

      {/* Medical Students Stats Grid */}
      <div className="stats-section">
        <h2 className="stats-section-title">Medical Students</h2>
        <div className="stats-grid">
          <div className="stat-card">
            <h3>Total Students</h3>
            <p className="stat-value">{studentStats.total || 0}</p>
          </div>
          <div className="stat-card pending">
            <h3>Pending</h3>
            <p className="stat-value">{studentStats.pending || 0}</p>
          </div>
          <div className="stat-card in-review">
            <h3>In Review</h3>
            <p className="stat-value">{studentStats.in_review || 0}</p>
          </div>
          <div className="stat-card approved">
            <h3>Approved</h3>
            <p className="stat-value">{studentStats.approved || 0}</p>
          </div>
          <div className="stat-card rejected">
            <h3>Rejected</h3>
            <p className="stat-value">{studentStats.rejected || 0}</p>
          </div>
          <div className="stat-card blocked">
            <h3>Blocked</h3>
            <p className="stat-value">{studentStats.blocked || 0}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <div className="filter-group">
          <label>Account Status:</label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All Status</option>
            <option value="pending_review">Pending Review</option>
            <option value="in_review">In Review</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="blocked">Blocked</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Verification:</label>
          <select value={verificationFilter} onChange={(e) => setVerificationFilter(e.target.value)}>
            <option value="all">All Verification</option>
            <option value="not_submitted">Not Submitted</option>
            <option value="submitted">Submitted</option>
            <option value="verified">Verified</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>

        <div className="filter-group search-group">
          <input
            type="text"
            placeholder="Search by name, email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      {/* Users Table */}
      {loading ? (
        <div className="loading-state">Loading users...</div>
      ) : error ? (
        <div className="error-state">{error}</div>
      ) : users.length === 0 ? (
        <div className="empty-state">No users found</div>
      ) : (
        <div className="table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>User Type</th>
                <th>Account Status</th>
                <th>Verification</th>
                <th>Documents</th>
                <th>Submitted</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>
                    <div className="user-info">
                      <strong>{user.user_type === 'doctor' ? 'Dr. ' : ''}{user.full_name}</strong>
                      <small>{user.specialities || 'N/A'}</small>
                    </div>
                  </td>
                  <td>{user.email}</td>
                  <td>
                    <span className={`user-type-badge ${user.user_type}`}>
                      {user.user_type === 'doctor' ? 'Doctor' : 'Medical Student'}
                    </span>
                  </td>
                  <td>{getStatusBadge(user.account_status)}</td>
                  <td>{getVerificationBadge(user.verification_status)}</td>
                  <td>
                    <span className="doc-count">{user.document_count || 0} docs</span>
                  </td>
                  <td>{user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon btn-view"
                        onClick={() => handleViewUser(user.id)}
                        title="View Details"
                      >
                        <ViewIcon />
                      </button>
                      {(user.account_status === 'pending_review' || user.account_status === 'in_review') && (
                        <>
                          <button
                            className="btn-icon btn-approve"
                            onClick={() => handleAction(user, 'approve')}
                            title="Approve"
                          >
                            <CheckIcon />
                          </button>
                          <button
                            className="btn-icon btn-reject"
                            onClick={() => handleAction(user, 'reject')}
                            title="Reject"
                          >
                            <CloseIcon />
                          </button>
                        </>
                      )}
                      {user.account_status === 'approved' && (
                        <button
                          className="btn-icon btn-suspend"
                          onClick={() => handleAction(user, 'suspend')}
                          title="Suspend"
                        >
                          <BanIcon />
                        </button>
                      )}
                      {user.account_status === 'blocked' && (
                        <button
                          className="btn-icon btn-approve"
                          onClick={() => handleAction(user, 'reactivate')}
                          title="Reactivate"
                        >
                          <CheckIcon />
                        </button>
                      )}
                      <button
                        className="btn-icon btn-note"
                        onClick={() => handleAction(user, 'note')}
                        title="Add Note"
                      >
                        <NoteIcon />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="btn-pagination"
          >
            Previous
          </button>
          <span className="page-info">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="btn-pagination"
          >
            Next
          </button>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="modal-content large-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>User Details - Dr. {selectedUser.full_name}</h2>
              <button className="btn-close" onClick={() => setShowDetailModal(false)}>
                <CloseIcon />
              </button>
            </div>
            <div className="modal-body">
              <div className="detail-section">
                <h3>Personal Information</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Full Name:</label>
                    <span>{selectedUser.full_name}</span>
                  </div>
                  <div className="detail-item">
                    <label>Email:</label>
                    <span>{selectedUser.email}</span>
                  </div>
                  <div className="detail-item">
                    <label>Phone:</label>
                    <span>{selectedUser.phone || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Speciality:</label>
                    <span>{selectedUser.speciality || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Account Status:</label>
                    {getStatusBadge(selectedUser.account_status)}
                  </div>
                  <div className="detail-item">
                    <label>Verification Status:</label>
                    {getVerificationBadge(selectedUser.verification_status)}
                  </div>
                </div>
              </div>

              {selectedUser.documents && selectedUser.documents.length > 0 && (
                <div className="detail-section">
                  <h3>Verification Documents ({selectedUser.documents.length})</h3>
                  <div className="documents-list">
                    {selectedUser.documents.map((doc, idx) => {
                      console.log('Document item:', doc);
                      console.log('Document URL:', doc.document_url);
                      return (
                      <div key={idx} className="document-item">
                        <div className="document-header">
                          <strong>{doc.document_type || 'Document'}</strong>
                          <span className={`doc-status ${doc.verification_status}`}>
                            {doc.verification_status || 'pending'}
                          </span>
                        </div>
                        {doc.document_url ? (
                          <div className="document-preview">
                            {doc.document_url.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                              <div>
                                <img
                                  src={doc.document_url}
                                  alt={doc.document_type}
                                  className="doc-image"
                                  onClick={() => handleImageClick(doc.document_url, doc.document_type)}
                                  onError={(e) => {
                                    console.error('Image failed to load:', doc.document_url);
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'block';
                                  }}
                                />
                                <div style={{display: 'none', padding: '20px', background: '#fee2e2', borderRadius: '8px'}}>
                                  <p style={{color: '#991b1b', margin: '0 0 10px 0'}}>Failed to load image</p>
                                  <a href={doc.document_url} target="_blank" rel="noopener noreferrer" className="btn-download">
                                    Open Image in New Tab
                                  </a>
                                </div>
                              </div>
                            ) : doc.document_url.match(/\.pdf$/i) ? (
                              <div className="pdf-viewer">
                                <iframe
                                  src={doc.document_url}
                                  title={doc.document_type}
                                  className="doc-pdf"
                                />
                                <a
                                  href={doc.document_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn-view-fullscreen"
                                >
                                  Open PDF in New Tab
                                </a>
                              </div>
                            ) : (
                              <a
                                href={doc.document_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn-download"
                              >
                                Download Document
                              </a>
                            )}
                          </div>
                        ) : (
                          <div className="document-preview">
                            <p style={{color: '#6b7280', fontStyle: 'italic'}}>No document uploaded</p>
                          </div>
                        )}
                        {doc.notes && (
                          <div className="document-notes">
                            <small><strong>Notes:</strong> {doc.notes}</small>
                          </div>
                        )}
                      </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {selectedUser.notes && selectedUser.notes.length > 0 && (
                <div className="detail-section">
                  <h3>Admin Notes</h3>
                  <div className="notes-list">
                    {selectedUser.notes.map((note, idx) => (
                      <div key={idx} className="note-item">
                        <p>{note.note}</p>
                        <small>By: {note.admin_name} on {new Date(note.created_at).toLocaleString()}</small>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedUser.activity && selectedUser.activity.length > 0 && (
                <div className="detail-section">
                  <h3>Activity Log</h3>
                  <div className="activity-list">
                    {selectedUser.activity.map((activity, idx) => (
                      <div key={idx} className="activity-item">
                        <strong>{activity.action_type.replace('_', ' ').toUpperCase()}</strong>
                        <p>{activity.details || 'No details'}</p>
                        <small>{new Date(activity.created_at).toLocaleString()}</small>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Action Modal */}
      {showActionModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowActionModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{actionType.charAt(0).toUpperCase() + actionType.slice(1)} User</h2>
              <button className="btn-close" onClick={() => setShowActionModal(false)}>
                <CloseIcon />
              </button>
            </div>
            <div className="modal-body">
              <p><strong>User:</strong> Dr. {selectedUser.full_name}</p>

              {(actionType === 'reject' || actionType === 'suspend') && (
                <div className="form-group">
                  <label>Reason *</label>
                  <textarea
                    value={actionData.reason}
                    onChange={(e) => setActionData({ ...actionData, reason: e.target.value })}
                    placeholder="Enter reason..."
                    rows="4"
                    required
                  />
                </div>
              )}

              {(actionType === 'approve' || actionType === 'reactivate' || actionType === 'note') && (
                <div className="form-group">
                  <label>Notes{actionType === 'note' ? ' *' : ''}</label>
                  <textarea
                    value={actionData.notes}
                    onChange={(e) => setActionData({ ...actionData, notes: e.target.value })}
                    placeholder="Enter notes..."
                    rows="4"
                    required={actionType === 'note'}
                  />
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowActionModal(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={submitAction}>
                Confirm {actionType.charAt(0).toUpperCase() + actionType.slice(1)}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Viewer Modal */}
      {showImageViewer && (
        <div className="image-viewer-overlay" onClick={handleCloseImageViewer}>
          <div className="image-viewer-content" onClick={(e) => e.stopPropagation()}>
            <div className="image-viewer-header">
              <h3>{viewerImage.title}</h3>
              <button className="btn-close" onClick={handleCloseImageViewer}>
                <CloseIcon />
              </button>
            </div>
            <div className="image-viewer-body">
              <img
                src={viewerImage.url}
                alt={viewerImage.title}
                style={{ transform: `scale(${imageZoom})` }}
                className="viewer-image"
              />
            </div>
            <div className="image-viewer-controls">
              <button className="zoom-btn" onClick={handleZoomOut} disabled={imageZoom <= 0.5}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"/>
                  <path d="M21 21l-4.35-4.35"/>
                  <line x1="8" y1="11" x2="14" y2="11"/>
                </svg>
                Zoom Out
              </button>
              <span className="zoom-level">{Math.round(imageZoom * 100)}%</span>
              <button className="zoom-btn" onClick={handleResetZoom}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M1 4v6h6"/>
                  <path d="M23 20v-6h-6"/>
                  <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"/>
                </svg>
                Reset
              </button>
              <button className="zoom-btn" onClick={handleZoomIn} disabled={imageZoom >= 3}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"/>
                  <path d="M21 21l-4.35-4.35"/>
                  <line x1="11" y1="8" x2="11" y2="14"/>
                  <line x1="8" y1="11" x2="14" y2="11"/>
                </svg>
                Zoom In
              </button>
              <a
                href={viewerImage.url}
                target="_blank"
                rel="noopener noreferrer"
                className="zoom-btn download-btn"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                Open in New Tab
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
